import { useCallback, useMemo, useState } from "react";

import { StringOptions } from "../../../components/Select";
import {
  ALL_VALUE,
  MONTH_CHOICE_OPTIONS,
  PERIOD_MONTH,
  PERIOD_YEAR,
  RANGE_OPTIONS,
} from "../components/TransactionsFilters/constants";
import { MonthOption } from "../components/TransactionsFilters/types";
import { PeriodState } from "../TransactionsPage/types";
import {
  MOBILE_MONTH_WINDOW,
  MONTH_WINDOW,
  getCurrentPeriod,
  getPeriodLabel,
  getTrailingMonths,
} from "../TransactionsPage/utils";

export interface UsePeriodStateReturn {
  period: PeriodState;
  periodLabel: string;
  isMonthPeriod: boolean;
  isYearPeriod: boolean;
  showYear: boolean;
  showMonthChoice: boolean;
  monthOptions: MonthOption[];
  activeMonth: MonthOption | null;
  rangeValue: StringOptions | null;
  yearValue: StringOptions;
  monthChoiceValue: StringOptions;
  onSelectMonth: (month: MonthOption) => void;
  onSelectRange: (option: StringOptions) => void;
  onSelectYear: (option: StringOptions) => void;
  onSelectMonthChoice: (option: StringOptions) => void;
  onResetPeriod: () => void;
}

export const usePeriodState = (
  isCompact: boolean,
  yearOptions: StringOptions[],
): UsePeriodStateReturn => {
  const [period, setPeriod] = useState<PeriodState>(getCurrentPeriod);

  const monthOptions = useMemo(
    () => getTrailingMonths(isCompact ? MOBILE_MONTH_WINDOW : MONTH_WINDOW),
    [isCompact],
  );

  const periodLabel = useMemo(() => getPeriodLabel(period), [period]);

  const isMonthPeriod = period.dateRange === PERIOD_MONTH;
  const isYearPeriod = period.dateRange === PERIOD_YEAR;
  const showYear = isYearPeriod || isMonthPeriod;
  const showMonthChoice = isMonthPeriod;

  const activeMonth = useMemo(() => {
    if (!isMonthPeriod) {
      return null;
    }
    return (
      monthOptions.find(
        (option) =>
          option.year === period.year && option.month === period.month,
      ) ?? null
    );
  }, [isMonthPeriod, monthOptions, period.year, period.month]);

  const rangeValue = useMemo(() => {
    if (isMonthPeriod) {
      return null;
    }
    return (
      RANGE_OPTIONS.find((option) => option.value === period.dateRange) ?? null
    );
  }, [isMonthPeriod, period.dateRange]);

  const yearValue = useMemo<StringOptions>(() => {
    const match = yearOptions.find(
      (option) => Number(option.value) === period.year,
    );
    return match ?? { label: String(period.year), value: String(period.year) };
  }, [yearOptions, period.year]);

  const monthChoiceValue = useMemo<StringOptions>(() => {
    if (!isMonthPeriod) {
      return MONTH_CHOICE_OPTIONS[0];
    }
    const match = MONTH_CHOICE_OPTIONS.find(
      (option) => Number(option.value) === period.month,
    );
    return match ?? MONTH_CHOICE_OPTIONS[0];
  }, [isMonthPeriod, period.month]);

  const onResetPeriod = useCallback(() => {
    setPeriod(getCurrentPeriod());
  }, []);

  const onSelectMonth = useCallback((month: MonthOption) => {
    setPeriod({
      dateRange: PERIOD_MONTH,
      year: month.year,
      month: month.month,
    });
  }, []);

  const onSelectRange = useCallback((option: StringOptions) => {
    const now = new Date();
    setPeriod({
      dateRange: option.value,
      year: now.getFullYear(),
      month: now.getMonth() + 1,
    });
  }, []);

  const onSelectYear = useCallback((option: StringOptions) => {
    const year = Number(option.value);
    setPeriod((prev) => {
      if (prev.dateRange === PERIOD_MONTH) {
        return { dateRange: PERIOD_MONTH, year, month: prev.month };
      }
      return { dateRange: PERIOD_YEAR, year, month: prev.month };
    });
  }, []);

  const onSelectMonthChoice = useCallback((option: StringOptions) => {
    setPeriod((prev) => {
      if (option.value === ALL_VALUE) {
        return { dateRange: PERIOD_YEAR, year: prev.year, month: prev.month };
      }
      return {
        dateRange: PERIOD_MONTH,
        year: prev.year,
        month: Number(option.value),
      };
    });
  }, []);

  return {
    period,
    periodLabel,
    isMonthPeriod,
    isYearPeriod,
    showYear,
    showMonthChoice,
    monthOptions,
    activeMonth,
    rangeValue,
    yearValue,
    monthChoiceValue,
    onSelectMonth,
    onSelectRange,
    onSelectYear,
    onSelectMonthChoice,
    onResetPeriod,
  };
};
